import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { resolvePostLoginPath } from "./lib/authRedirect";

// Admin Pages
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import CreateEvent from "./admin/CreateEvent";
import AdminSidebar from "./admin/AdminSidebar";
import Customers from "./admin/Customers";
import Accounts from "./admin/Accounts";
import LiveEvents from "./admin/LiveEvents";
import AdminProfile from "./admin/AdminProfile";
import Orders from "./admin/Orders";
import Tickets from "./admin/Tickets";
import FeaturedEvents from "./admin/FeaturedEvents";
import Venues from "./admin/Venues";
import VenueEditor from "./admin/VenueEditor";
import Bookings from "./admin/Bookings";
import BookingDetails from "./admin/BookingDetails";
import Support from "./admin/Support";
import Settings from "./admin/Settings";
import Projects from "./admin/Projects";
import ProjectDetails from "./admin/ProjectDetails";
import Clients from "./admin/Clients";

// Public/User Pages
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import HomePage from "./pages/HomePage";
import About from "./pages/About";
import Faqs from "./pages/Faqs";
import Events from "./pages/Events";
import EventDetails from "./pages/EventDetails";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Ticket from "./pages/Ticket";
import MyTickets from "./pages/MyTickets";
import VenueSeatPlans from "./pages/VenueSeatPlans";
import StaffScanner from "./pages/StaffScanner";

// Layouts
import PublicLayout from "./layouts/PublicLayout";
import MaintenancePage from "./pages/Maintenance";
import NotFound from "./pages/NotFound";
import {
  PLATFORM_SETTINGS_UPDATED_EVENT,
  getCachedPlatformSettings,
  loadPlatformSettings,
} from "./lib/platformSettings";
import { resolveAdminHostAction } from "./lib/adminHostGate";

/* ================= HELPERS ================= */

const getStoredFlag = (key) => {
  try {
    return localStorage.getItem(key) === "true";
  } catch (error) {
    console.warn(`Unable to read ${key} from localStorage:`, error);
    return false;
  }
};

const normalizeRole = (value) => {
  const role = String(value || "").trim().toLowerCase();
  if (role === "superadmin") return "super_admin";
  return role;
};

const isAdminRole = (value) => {
  const normalized = normalizeRole(value);
  return normalized === "admin" || normalized === "super_admin";
};

const resolveRoleFromMetadata = (user) => {
  const appMetadata = user?.app_metadata || {};
  const userMetadata = user?.user_metadata || {};

  const directCandidates = [appMetadata.role, userMetadata.role];
  for (const candidate of directCandidates) {
    if (isAdminRole(candidate)) return normalizeRole(candidate);
  }

  const listCandidates = [appMetadata.roles, userMetadata.roles];
  for (const listValue of listCandidates) {
    if (!Array.isArray(listValue)) continue;
    const hit = listValue.find((item) => isAdminRole(item));
    if (hit) return normalizeRole(hit);
  }

  return null;
};

// Fixes the issue where navigating to a new page stays at the bottom
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/* ================= PROTECTION HELPERS ================= */

const isMaintenanceAllowedPath = (pathname) => {
  if (pathname === "/admin-login" || pathname === "/admin") return true;
  if (pathname.startsWith("/admin/")) return true;
  if (pathname === "/staff") return true;
  if (pathname === "/auth/callback" || pathname === "/reset-password") return true;
  return false;
};

const HostGate = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const action = resolveAdminHostAction({
    hostname,
    pathname,
    search,
    hash,
  });
  const redirectTo = action?.type === "redirect" ? action.to : null;
  const isExternalRedirect = Boolean(
    redirectTo && (redirectTo.startsWith("https://") || redirectTo.startsWith("http://"))
  );

  useEffect(() => {
    if (isExternalRedirect) {
      window.location.replace(redirectTo);
    }
  }, [isExternalRedirect, redirectTo]);

  if (!action) return children;

  if (action.type === "notfound") {
    return <NotFound />;
  }

  if (isExternalRedirect) {
    return (
      <div className="min-h-screen bg-[#0b0b0d]" aria-busy="true" aria-label="Redirecting" />
    );
  }

  return <Navigate replace to={redirectTo} />;
};

const MaintenanceGate = ({ children }) => {
  const { pathname } = useLocation();
  const [gateState, setGateState] = useState(() => {
    const cached = getCachedPlatformSettings();
    if (!cached) return "checking";
    return cached.maintenanceMode ? "blocked" : "open";
  });

  useEffect(() => {
    if (isMaintenanceAllowedPath(pathname) || getStoredFlag("isAdminAuthenticated")) {
      setGateState("open");
      return undefined;
    }

    let isMounted = true;
    const cached = getCachedPlatformSettings();
    if (cached) {
      setGateState(cached.maintenanceMode ? "blocked" : "open");
    } else {
      setGateState("checking");
    }

    const applyMaintenance = (enabled) => {
      if (!isMounted) return;
      setGateState(enabled ? "blocked" : "open");
    };

    const load = async () => {
      try {
        const settings = await loadPlatformSettings();
        applyMaintenance(Boolean(settings.maintenanceMode));
      } catch (error) {
        console.warn("Could not load maintenance settings:", error);
        applyMaintenance(false);
      }
    };

    const onUpdated = (event) => {
      applyMaintenance(Boolean(event.detail?.maintenanceMode));
    };

    load();
    window.addEventListener(PLATFORM_SETTINGS_UPDATED_EVENT, onUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(PLATFORM_SETTINGS_UPDATED_EVENT, onUpdated);
    };
  }, [pathname]);

  if (isMaintenanceAllowedPath(pathname) || getStoredFlag("isAdminAuthenticated")) {
    return children;
  }

  if (gateState === "checking") {
    return (
      <div className="min-h-screen bg-[#0b0b0d]" aria-busy="true" aria-label="Checking site availability" />
    );
  }

  if (gateState === "blocked") {
    return <MaintenancePage />;
  }

  return children;
};

const ProtectedAdminRoute = ({ children }) => {
  const isAdminAuthenticated = getStoredFlag("isAdminAuthenticated");
  if (!isAdminAuthenticated) return <Navigate replace to="/admin-login" />;

  return (
    <div className="flex min-h-screen bg-dashboard-bg">
      <AdminSidebar />
      <div className="flex-1 min-w-0 overflow-y-auto h-screen">{children}</div>
    </div>
  );
};

const AdminSessionGate = ({ children }) => {
  const [sessionState, setSessionState] = useState("checking");

  useEffect(() => {
    let isMounted = true;
    let inFlight = false;

    const syncSession = async () => {
      if (inFlight) return;
      inFlight = true;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        const hasSession = Boolean(session?.user);
        if (!hasSession) {
          localStorage.removeItem("isAdminAuthenticated");
          localStorage.removeItem("adminRole");
          setSessionState("guest");
          return;
        }

        const authUser = session.user;
        let resolvedRole = resolveRoleFromMetadata(authUser);

        if (!isAdminRole(resolvedRole)) {
          const { data: profileRow, error: profileError } = await supabase
            .from("users")
            .select("role")
            .eq("id", authUser.id)
            .maybeSingle();

          if (profileError) {
            console.warn("Unable to validate admin role from users table:", profileError);
          }

          resolvedRole = profileRow?.role || resolvedRole;
        }

        if (!isMounted) return;

        if (!isAdminRole(resolvedRole)) {
          localStorage.removeItem("isAdminAuthenticated");
          localStorage.removeItem("adminRole");
          setSessionState("guest");
          return;
        }

        localStorage.setItem("isAdminAuthenticated", "true");
        localStorage.setItem("adminRole", normalizeRole(resolvedRole));
        setSessionState("authed");
      } finally {
        inFlight = false;
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      if (!isMounted) return;
      syncSession();
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  if (sessionState === "checking") {
    return <div className="min-h-screen bg-dashboard-bg" />;
  }

  if (sessionState !== "authed") {
    return <Navigate replace to="/admin-login" />;
  }

  return children;
};

const UserSessionGate = ({ children, requireAuth }) => {
  const [sessionState, setSessionState] = useState("checking");
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const syncSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const hasSession = Boolean(session?.user);

      if (hasSession) {
        localStorage.setItem("userAuthenticated", "true");
      } else {
        localStorage.removeItem("userAuthenticated");
      }

      if (isMounted) {
        setSessionState(hasSession ? "authed" : "guest");
      }
    };

    syncSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const hasSession = Boolean(session?.user);

      if (hasSession) {
        localStorage.setItem("userAuthenticated", "true");
      } else {
        localStorage.removeItem("userAuthenticated");
      }

      if (isMounted) {
        setSessionState(hasSession ? "authed" : "guest");
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  if (sessionState === "checking") {
    return <div className="min-h-screen bg-[#121212]" />;
  }

  if (requireAuth && sessionState !== "authed") {
    return <Navigate replace to="/login" />;
  }

  if (!requireAuth && sessionState === "authed") {
    return <Navigate replace to={resolvePostLoginPath(location.state?.from)} />;
  }

  return children;
};

const ProtectedUserRoute = ({ children }) => (
  <UserSessionGate requireAuth>{children}</UserSessionGate>
);

const PublicOnlyRoute = ({ children }) => (
  <UserSessionGate requireAuth={false}>{children}</UserSessionGate>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <HostGate>
      <MaintenanceGate>
      <Routes>
        {/* ================= PUBLIC ROUTES ================= */}
        <Route 
          path="/" 
          element={
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          } 
        />

        <Route 
          path="/login" 
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          } 
        />
        
        <Route 
          path="/signup" 
          element={
            <PublicOnlyRoute>
              <Signup />
            </PublicOnlyRoute>
          } 
        />

        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route 
          path="/events" 
          element={
            <PublicLayout>
              <Events />
            </PublicLayout>
          } 
        />

        <Route
          path="/about"
          element={
            <PublicLayout>
              <About />
            </PublicLayout>
          }
        />

        <Route
          path="/faqs"
          element={
            <PublicLayout>
              <Faqs />
            </PublicLayout>
          }
        />

        <Route 
          path="/event/:id" 
          element={<EventDetails />}
        />

        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        
        <Route
          path="/venue-seat-plans"
          element={<VenueSeatPlans />}
        />
        
        <Route 
          path="/ticket/:id" 
          element={<Ticket />}
        />

        <Route
          path="/tickets"
          element={
            <PublicLayout>
              <ProtectedUserRoute>
                <MyTickets />
              </ProtectedUserRoute>
            </PublicLayout>
          }
        />

        {/* ================= PROTECTED USER ROUTES ================= */}
        <Route
          path="/profile"
          element={
            <PublicLayout>
              <ProtectedUserRoute>
                <Profile />
              </ProtectedUserRoute>
            </PublicLayout>
          }
        />

        {/* ================= ADMIN ROUTES ================= */}
        <Route path="/staff" element={<StaffScanner />} />

        <Route path="/admin-login" element={<AdminLogin />} />

        <Route path="/admin" element={<Navigate replace to="/admin-login" />} />
        <Route path="/admin/dashboard" element={<AdminSessionGate><ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/bookings" element={<AdminSessionGate><ProtectedAdminRoute><Bookings /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/bookings/:id" element={<AdminSessionGate><ProtectedAdminRoute><BookingDetails /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/create-event" element={<AdminSessionGate><ProtectedAdminRoute><CreateEvent /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/customers" element={<AdminSessionGate><ProtectedAdminRoute><Customers /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/accounts" element={<AdminSessionGate><ProtectedAdminRoute><Accounts /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/profile" element={<AdminSessionGate><ProtectedAdminRoute><AdminProfile /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/liveevents" element={<AdminSessionGate><ProtectedAdminRoute><LiveEvents /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/orders" element={<AdminSessionGate><ProtectedAdminRoute><Orders /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/tickets" element={<AdminSessionGate><ProtectedAdminRoute><Tickets /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/featured" element={<AdminSessionGate><ProtectedAdminRoute><FeaturedEvents /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/venues/new" element={<AdminSessionGate><ProtectedAdminRoute><VenueEditor /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/venues/:id" element={<AdminSessionGate><ProtectedAdminRoute><VenueEditor /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/venues" element={<AdminSessionGate><ProtectedAdminRoute><Venues /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/support" element={<AdminSessionGate><ProtectedAdminRoute><Support /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/live-events" element={<Navigate replace to="/admin/liveevents" />} />
        <Route path="/admin/settings" element={<AdminSessionGate><ProtectedAdminRoute><Settings /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/projects" element={<AdminSessionGate><ProtectedAdminRoute><Projects /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/projects/:id" element={<AdminSessionGate><ProtectedAdminRoute><ProjectDetails /></ProtectedAdminRoute></AdminSessionGate>} />
        <Route path="/admin/clients" element={<AdminSessionGate><ProtectedAdminRoute><Clients /></ProtectedAdminRoute></AdminSessionGate>} />


        {/* ================= FALLBACK ================= */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </MaintenanceGate>
      </HostGate>
    </Router>
  );
}

export default App;